import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useOrders } from '@hooks';
import { Card, Descriptions, Empty, Spin, Table, Tag, Typography } from 'antd';
import { useParams, useSearchParams } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

export const OrderPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken');

  const { getOrder } = useOrders();
  const { data: order, isLoading, error } = getOrder(orderId!, accessToken || undefined);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'pending':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'cancelled':
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
      case 'failed':
        return 'error';
      default:
        return 'processing';
    }
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center' as const,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      align: 'right' as const,
      render: (price: number) => formatPrice(price),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      align: 'right' as const,
      render: (_: any, record: any) => formatPrice(record.price * record.quantity),
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Empty
            description={
              <span className="text-gray-400 text-lg">
                Order not found or you don't have permission to view it
              </span>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Title level={1} className="!text-white mb-4 font-android">
            Order Details
          </Title>
          <div className="flex items-center justify-center gap-3 mb-4">
            {getStatusIcon(order.status)}
            <Tag color={getStatusColor(order.status)} className="text-lg px-4 py-1">
              {order.status.toUpperCase()}
            </Tag>
          </div>
          <Text className="text-gray-400 text-lg">
            Order #{order.orderNumber}
          </Text>
        </div>

        {/* Order Information */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <Descriptions
            title={<span className="text-white font-android">Order Information</span>}
            column={1}
            labelStyle={{ color: '#9ca3af' }}
            contentStyle={{ color: '#ffffff' }}
          >
            <Descriptions.Item label="Order Number">
              {order.orderNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Order Date">
              {formatDate(order.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {order.customerEmail}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(order.status)}>
                {order.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Order Items */}
        <Card
          className="bg-zinc-900 border-zinc-800 mb-6"
          title={<span className="text-white font-android">Order Items</span>}
        >
          <Table
            columns={columns}
            dataSource={order.items}
            pagination={false}
            rowKey="id"
            className="order-items-table"
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-zinc-800">
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong className="text-white text-lg">Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong className="text-yellow-principal text-lg">
                      {formatPrice(order.totalAmount)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        {/* Additional Info */}
        <Card className="bg-zinc-900 border-zinc-800">
          <Paragraph className="text-gray-300 mb-0">
            If you have any questions about your order, please contact our support team
            and reference your order number: <Text strong className="text-white">{order.orderNumber}</Text>
          </Paragraph>
        </Card>
      </div>
    </div>
  );
};



